import { ApiResult } from '@src/api/apiClient/apiResult';
import { axiosClient } from '@src/api/apiClient/axiosClient';
import { IndexRequest } from '@src/api/types/shared/request';

import { Pagination } from '../../types/pagination';

export type ChannelMember = {
  id: number;
  avatar_url: string | null;
  display_name: string;
  company_name: string | null;
  affiliation: string | null;
  position: string | null;
  industry: string | null;
  member_id: number | null;
  user_type: string;
  channel: {
    code: string;
    visibility: number;
  };
  role: {
    id: number;
    name: string;
    code: string;
  };
};

export type SuggestedUser = {
  id: number;
  name: string;
  email: string;
  avatar_url: string;
  user_type: string;
  display_name: string;
};

export type ChannelSection = {
  name: string;
  code: string;
  description: string;
  visibility: number;
  sort_order: number;
  members_count: number;
};

export type ChannelMemberIndexResponse = {
  pagination: Pagination;
  data: {
    tenant_admin_users_count: number;
    members: ChannelMember[];
  };
};

export type ChannelMemberShowResponse = {
  avatar_url?: string;
  display_name: string;
  company_name: string;
  affiliation?: string;
  position?: string;
  introduction?: string;
  industry_text?: string;
  channel_section: ChannelSection[];
};

export type ChannelMemberSuggestResponse = SuggestedUser[];

export type ChannelMemberRequest = {
  user_id: number;
  user_type: string;
};

export type ChannelMemberSuggestRequest = {
  keyword?: string;
};

export type ChannelMemberInviteRequest = {
  channel_members: ChannelMemberRequest[];
};

export type ChannelMemberResponse = unknown;

export type ChannelMemberErrorResponse = {
  channel_members: Array<{
    user_id?: string[];
    user_type?: string[];
  }>;
};

export const channelMemberApiClient = {
  index: async (
    code: string,
    params: IndexRequest = {},
  ): Promise<ApiResult<ChannelMemberIndexResponse>> => {
    return await axiosClient.get(`/community/channels/${code}/channel-members`, { params });
  },
  show: async (code: string, memberId: number): Promise<ApiResult<ChannelMemberShowResponse>> => {
    return await axiosClient.get(`/community/channels/${code}/channel-members/${memberId}`);
  },
  publicChannelTenantUserShow: async (
    code: string,
    userId: number,
  ): Promise<ApiResult<ChannelMemberShowResponse>> => {
    return await axiosClient.get(`/community/channels/${code}/channel-members/tenant/${userId}`);
  },
  publicChannelCompanyUserShow: async (
    code: string,
    userId: number,
  ): Promise<ApiResult<ChannelMemberShowResponse>> => {
    return await axiosClient.get(`/community/channels/${code}/channel-members/company/${userId}`);
  },
  suggest: async (
    code: string,
    params: ChannelMemberSuggestRequest,
  ): Promise<ApiResult<ChannelMemberSuggestResponse>> => {
    return await axiosClient.get(`/community/channels/${code}/channel-members/suggest`, { params });
  },
  invite: async (
    params: ChannelMemberInviteRequest,
    code: string,
  ): Promise<ApiResult<ChannelMemberResponse, ChannelMemberErrorResponse>> => {
    return await axiosClient.post(`/community/channels/${code}/channel-members/invite`, params);
  },
  delete: async (code: string, memberId: number): Promise<ApiResult<ChannelMemberResponse>> => {
    return await axiosClient.delete(`/community/channels/${code}/channel-members/${memberId}`);
  },
};
